import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ClientLogo } from '../components/ClientLogo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  CaretLeft, UserPlus, Trash, Plus, GearSix, UsersThree, Columns, TextAa, CalendarBlank, Hash, ListDashes, Image as ImageIcon, Layout,
} from '@phosphor-icons/react';
import FieldLayoutEditor from '../components/FieldLayoutEditor';
import ProjectIntegrationsSection from '../components/ProjectIntegrationsSection';
import ProjectFiltersManager from '../components/ProjectFiltersManager';
import { useConfirm } from '../components/ConfirmDialog';
import { toast } from 'sonner';

export default function ProjectSettingsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentOrg, getOrgHeaders, API, user } = useAuth();
  const confirm = useConfirm();
  const [project, setProject] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [orgTimezone, setOrgTimezone] = useState('UTC');
  const [clients, setClients] = useState([]);
  // Inline rename state for the custom-fields table. `editingFieldId` is the
  // row currently in edit-mode; the Input below tracks the draft name.
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editingFieldName, setEditingFieldName] = useState('');

  // Add member dialog
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberId, setAddMemberId] = useState('');
  const [addMemberSearch, setAddMemberSearch] = useState('');

  // Add custom field dialog
  const [showAddField, setShowAddField] = useState(false);
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [fieldOptions, setFieldOptions] = useState('');
  const [fieldWorkTypes, setFieldWorkTypes] = useState([]);

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  useEffect(() => {
    if (!currentOrg) return;
    axios.get(`${API}/clients`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setClients(r.data || [])).catch(() => setClients([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg]);

  const fetchAll = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const [pRes, pmRes, omRes, cfRes, tRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, h()),
        axios.get(`${API}/projects/${projectId}/members`, h()),
        axios.get(`${API}/members`, h()),
        axios.get(`${API}/projects/${projectId}/custom-fields`, h()),
        axios.get(`${API}/projects/${projectId}/tasks`, h()).catch(() => ({ data: [] })),
      ]);
      setProject(pRes.data);
      setProjectMembers(pmRes.data);
      setOrgMembers(omRes.data);
      setCustomFields(cfRes.data);
      setTasks(tRes.data || []);
    } catch (err) { console.error(err); }
  }, [projectId, currentOrg, API, h]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Workspace timezone — used for the custom-filter date-range preview.
  useEffect(() => {
    if (!currentOrg) return;
    axios.get(`${API}/organizations/current/settings`, h())
      .then(({ data }) => { if (data?.timezone) setOrgTimezone(data.timezone); })
      .catch(() => {});
  }, [currentOrg, API, h]);

  // Deep-link support: /projects/:id/settings#custom-filters scrolls to the
  // Custom Filters section (linked from the board's "Manage custom filters").
  useEffect(() => {
    if (window.location.hash !== '#custom-filters') return;
    const t = setTimeout(() => {
      document.getElementById('custom-filters')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => clearTimeout(t);
  }, [project]);

  // Project Settings is admin-only territory. Contributors and viewers must
  // be bounced back to the board. Backend already 403s on the mutation
  // endpoints, but this prevents the page from even appearing.
  useEffect(() => {
    if (!currentOrg) return;
    const role = currentOrg.role;
    if (role && !['owner', 'admin', 'project_manager'].includes(role)) {
      navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [currentOrg, navigate, projectId]);

  const handleAddMember = async () => {
    if (!addMemberId) return;
    try {
      await axios.post(`${API}/projects/${projectId}/members`, { user_id: addMemberId }, h());
      setAddMemberId('');
      setShowAddMember(false);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const handleRemoveMember = async (userId) => {
    if (!(await confirm({ title: 'Remove this member?', description: 'They will lose access to this project.', confirmText: 'Remove', variant: 'destructive' }))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/members/${userId}`, h());
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleAddField = async () => {
    if (!fieldName.trim() || !fieldType) return;
    try {
      await axios.post(`${API}/projects/${projectId}/custom-fields`, {
        name: fieldName.trim(),
        type: fieldType,
        options: fieldType === 'select' ? fieldOptions.split(',').map(o => o.trim()).filter(Boolean) : [],
        work_types: fieldWorkTypes,
      }, h());
      setFieldName('');
      setFieldType('text');
      setFieldOptions('');
      setFieldWorkTypes([]);
      setShowAddField(false);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const handleRemoveField = async (fieldId) => {
    if (!(await confirm({ title: 'Remove this custom field?', description: 'Values stored in this field on existing tasks will no longer be visible.', confirmText: 'Remove field', variant: 'destructive' }))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/custom-fields/${fieldId}`, h());
      fetchAll();
    } catch (err) { console.error(err); }
  };

  // Delete the entire project. Owners and admins only (backend also enforces).
  // We redirect to Projects list (home) on success since the current page
  // effectively vanishes.
  const handleDeleteProject = async () => {
    const ok = await confirm({
      title: `Delete project "${project?.name}"?`,
      description: 'This will soft-delete the project along with all its tasks, sprints, comments, and integrations. This cannot be easily undone.',
      confirmText: 'Delete project', variant: 'destructive',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, h());
      // Tell the sidebar to refetch its project list so the deleted entry
      // disappears immediately instead of waiting for a manual refresh.
      window.dispatchEvent(new Event('project-deleted'));
      toast.success('Project deleted');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete project');
    }
  };

  const nonMemberOrg = orgMembers.filter(om => !projectMembers.find(pm => pm.user_id === om.user_id));
  const canManage = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);

  const FIELD_TYPE_ICONS = { text: TextAa, number: Hash, date: CalendarBlank, select: ListDashes, richtext: ImageIcon };

  const toggleWorkType = (wt) => {
    setFieldWorkTypes(prev => prev.includes(wt) ? prev.filter(x => x !== wt) : [...prev, wt]);
  };

  return (
    <div className="flex-1 overflow-auto bg-white" data-testid="project-settings-page">
      {/* Header */}
      <div className="px-8 py-5 border-b border-zinc-200 flex items-center gap-3">
        <button onClick={() => navigate(`/projects/${projectId}`)} className="flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-800 transition-colors" data-testid="back-to-board">
          <CaretLeft size={14} /> Back to Board
        </button>
        <Separator orientation="vertical" className="h-5" />
        <GearSix size={16} className="text-zinc-500" />
        <h1 className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight">{project?.name} Settings</h1>
      </div>

      <div className="p-8 max-w-3xl space-y-10">
        {/* Section: Project details (rename) — owner/admin/PM only.
            Inline edit pattern: click the name → swap to Input → blur saves.
            Mirrors how the body uses onBlur autosave elsewhere so users get
            a consistent low-friction interaction. */}
        <section data-testid="project-details-section">
          <div className="flex items-center gap-2 mb-3">
            <GearSix size={18} className="text-zinc-500" />
            <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Project Details</h2>
          </div>
          <div className="space-y-4 rounded-xl border border-zinc-200 bg-white p-5">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-medium">Name</Label>
              <Input
                data-testid="project-name-input"
                disabled={!canManage}
                value={project?.name || ''}
                onChange={e => setProject(p => ({ ...p, name: e.target.value }))}
                onBlur={async (e) => {
                  const v = e.target.value.trim();
                  if (!v || v === (project?._original_name ?? '')) return;
                  try {
                    const { data } = await axios.put(`${API}/projects/${projectId}`, { name: v }, h());
                    setProject({ ...data, _original_name: data.name });
                    toast.success('Project name updated');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to rename project');
                    fetchAll();
                  }
                }}
                className="mt-1 max-w-md"
                maxLength={100}
              />
              <p className="text-[11px] text-zinc-500 mt-1">Visible to all project members. Changes save automatically.</p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-medium">Key</Label>
              <div className="mt-1 inline-flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 h-9 rounded-md bg-zinc-100 text-zinc-700 font-mono text-[13px] font-semibold" data-testid="project-key-readonly">
                  {project?.key}
                </span>
                <span className="text-[11px] text-zinc-500">Read-only — used in all task IDs (e.g. <code>{project?.key}-1</code>)</span>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-medium">Description</Label>
              <Input
                data-testid="project-description-input"
                disabled={!canManage}
                value={project?.description || ''}
                onChange={e => setProject(p => ({ ...p, description: e.target.value }))}
                onBlur={async (e) => {
                  const v = e.target.value;
                  if (v === (project?._original_description ?? '')) return;
                  try {
                    const { data } = await axios.put(`${API}/projects/${projectId}`, { description: v }, h());
                    setProject({ ...data, _original_description: data.description });
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to save description');
                    fetchAll();
                  }
                }}
                placeholder="Optional short description"
                className="mt-1"
                maxLength={500}
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Client</Label>
              <div className="flex items-center gap-2.5 mt-1">
                {project?.client && <ClientLogo client={project.client} size={36} rounded="rounded-md" />}
                <select
                  disabled={!canManage}
                  data-testid="settings-client-select"
                  value={project?.client_id || ''}
                  onChange={async (e) => {
                    const v = e.target.value || null;
                    try {
                      const { data } = await axios.put(`${API}/projects/${projectId}`, { client_id: v }, h());
                      setProject(p => ({ ...data, _original_description: p._original_description }));
                      toast.success(v ? 'Client assigned' : 'Client removed');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Failed to update client');
                    }
                  }}
                  className="flex-1 h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-800 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                >
                  <option value="">No client — internal project</option>
                  {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">The client's logo &amp; colour brand this board for your team and the customer.</p>
            </div>
          </div>
        </section>

        {/* Section: Project Members */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UsersThree size={18} className="text-zinc-500" />
              <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Project Members</h2>
              <Badge className="text-[10px] bg-zinc-100 text-zinc-500 border border-zinc-200">{projectMembers.length}</Badge>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => setShowAddMember(true)} className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-[11px]" data-testid="add-project-member-btn">
                <UserPlus size={13} className="mr-1" /> Add Member
              </Button>
            )}
          </div>
          <div className="border border-zinc-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">Member</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">Email</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium w-28">Role</TableHead>
                  {canManage && <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium w-16"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {projectMembers.map(m => (
                  <TableRow key={m.user_id} className="hover:bg-zinc-50" data-testid={`proj-member-${m.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6"><AvatarImage src={m.avatar} /><AvatarFallback className="bg-blue-100 text-blue-700 text-[9px] font-semibold">{m.name?.charAt(0)}</AvatarFallback></Avatar>
                        <span className="text-[13px] font-medium text-zinc-800">{m.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[12px] text-zinc-500">{m.email}</TableCell>
                    <TableCell><Badge className="text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200 capitalize">{m.role}</Badge></TableCell>
                    {canManage && (
                      <TableCell>
                        {/* Owners are never removable. PMs cannot remove themselves
                            (would lock them out of the project). */}
                        {m.role !== 'owner' && !(currentOrg?.role === 'project_manager' && m.user_id === user?.user_id) && (
                          <button onClick={() => handleRemoveMember(m.user_id)} className="text-zinc-300 hover:text-red-500 transition-colors" data-testid={`remove-member-${m.user_id}`}><Trash size={14} /></button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        <Separator />

        {/* Section: Custom Fields */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Columns size={18} className="text-zinc-500" />
              <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Custom Fields</h2>
              <Badge className="text-[10px] bg-zinc-100 text-zinc-500 border border-zinc-200">{customFields.length}</Badge>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => setShowAddField(true)} className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-[11px]" data-testid="add-custom-field-btn">
                <Plus size={13} className="mr-1" /> Add Field
              </Button>
            )}
          </div>
          {customFields.length === 0 ? (
            <div className="border border-zinc-200 rounded-lg p-8 text-center">
              <Columns size={32} className="text-zinc-300 mx-auto mb-3" />
              <p className="text-[13px] text-zinc-500 mb-1">No custom fields yet</p>
              <p className="text-[11px] text-zinc-400">Add custom fields to track additional data on tasks</p>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">Field Name</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium w-28">Type</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium w-40">Applies To</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium w-32">Options</TableHead>
                    {canManage && <TableHead className="w-12"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customFields.map(f => {
                    const Icon = FIELD_TYPE_ICONS[f.type] || TextAa;
                    const isEditing = editingFieldId === f.field_id;
                    return (
                      <TableRow key={f.field_id} className="hover:bg-zinc-50" data-testid={`custom-field-${f.field_id}`}>
                        <TableCell className="text-[13px] font-medium text-zinc-800">
                          {isEditing ? (
                            <Input
                              autoFocus
                              value={editingFieldName}
                              maxLength={60}
                              onChange={e => setEditingFieldName(e.target.value)}
                              onBlur={async () => {
                                const v = editingFieldName.trim();
                                if (!v || v === f.name) { setEditingFieldId(null); return; }
                                try {
                                  await axios.put(`${API}/projects/${projectId}/custom-fields/${f.field_id}`, { name: v }, h());
                                  toast.success('Field renamed');
                                  fetchAll();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Failed to rename field');
                                } finally {
                                  setEditingFieldId(null);
                                }
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') e.currentTarget.blur();
                                if (e.key === 'Escape') setEditingFieldId(null);
                              }}
                              className="h-7 text-[13px]"
                              data-testid={`custom-field-name-input-${f.field_id}`}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => { if (canManage) { setEditingFieldId(f.field_id); setEditingFieldName(f.name); } }}
                              disabled={!canManage}
                              className="hover:text-blue-600 disabled:cursor-default"
                              title={canManage ? 'Click to rename' : ''}
                              data-testid={`custom-field-name-${f.field_id}`}
                            >
                              {f.name}
                            </button>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-[12px] text-zinc-600"><Icon size={13} /> {f.type}</span>
                        </TableCell>
                        <TableCell>
                          {f.work_types?.length > 0 ? f.work_types.map(wt => (
                            <Badge key={wt} className="text-[9px] bg-zinc-100 text-zinc-500 mr-1 capitalize">{wt}</Badge>
                          )) : <span className="text-[11px] text-zinc-400">All types</span>}
                        </TableCell>
                        <TableCell className="text-[11px] text-zinc-500 truncate max-w-[120px]">{f.options?.join(', ') || '-'}</TableCell>
                        {canManage && (
                          <TableCell><button onClick={() => handleRemoveField(f.field_id)} className="text-zinc-300 hover:text-red-500 transition-colors"><Trash size={14} /></button></TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        <Separator />

        {/* Custom Statuses */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Custom Statuses</h2>
            <Badge className="text-[10px] bg-zinc-100 text-zinc-500 border border-zinc-200">{(project?.custom_statuses || []).length}</Badge>
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {['To Do', 'In Progress', 'Review', 'Done'].map(s => (
                <Badge key={s} className="text-[11px] bg-zinc-100 text-zinc-600 border border-zinc-200">{s} (default)</Badge>
              ))}
              {(project?.custom_statuses || []).map(s => (
                <Badge key={s.id} className="text-[11px] border flex items-center gap-1" style={{ backgroundColor: s.color + '20', color: s.color, borderColor: s.color + '40' }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}
                  {canManage && <button onClick={async () => { await axios.delete(`${API}/projects/${projectId}/statuses/${s.id}`, h()); fetchAll(); }} className="ml-1 hover:text-red-600"><Trash size={10} /></button>}
                </Badge>
              ))}
            </div>
            <p className="text-[11px] text-zinc-400">Custom statuses can be added directly from the board view using the "Create column" button.</p>
          </div>
        </section>

        <Separator />

        {/* Field Layout Configurator */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Layout size={18} className="text-zinc-500" />
            <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Field Layout</h2>
          </div>
          <p className="text-[11px] text-zinc-500 mb-4">Configure which fields appear in the main content area vs. the side panel for each work type. Drag fields between zones to customize the task detail layout.</p>
          <FieldLayoutEditor
            fieldLayouts={project?.field_layouts || {}}
            customFields={customFields}
            onSave={async (newLayouts) => {
              try {
                await axios.put(`${API}/projects/${projectId}/field-layouts`, { field_layouts: newLayouts }, h());
                fetchAll();
              } catch (err) { console.error(err); }
            }}
          />
        </section>

        <Separator />

        {/* Card Layout — controls which fields appear on Kanban cards.
            Owners/Admins/PMs only. Defaults to the canonical 5: key, due,
            priority, story points, assignee. */}
        <section data-testid="card-layout-section">
          <div className="flex items-center gap-2 mb-4">
            <Columns size={18} className="text-zinc-500" />
            <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Card Layout</h2>
          </div>
          <p className="text-[11px] text-zinc-500 mb-4">Choose which fields appear on the bottom strip of each Kanban card. Same idea as column visibility — toggle what your team needs at a glance.</p>
          <CardLayoutEditor
            value={project?.card_fields}
            customFields={project?.custom_fields}
            canEdit={canManage}
            onSave={async (next) => {
              try {
                await axios.patch(`${API}/projects/${projectId}/card-fields`, { card_fields: next }, h());
                fetchAll();
                toast.success('Card layout updated');
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to update card layout');
              }
            }}
          />
        </section>

        <Separator />

        {/* Time tracking — converts story points to hours for estimate auto-fill + hour-based burndown */}
        <section data-testid="time-tracking-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-['Outfit'] text-base font-semibold text-zinc-900">Time tracking</h2>
          </div>
          <div className="border border-zinc-200 rounded-lg p-4 bg-white">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-[12px] font-semibold text-zinc-900">Hours per story point</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  When set, new tasks auto-fill their Original Estimate as <span className="font-mono">story_points × hours</span>. Leave blank to disable hour tracking for this project.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number" step="0.25" min="0.25" max="40"
                  data-testid="hours-per-sp-input"
                  disabled={!canManage}
                  placeholder="4"
                  value={project?.hours_per_story_point ?? ''}
                  onChange={async (e) => {
                    const raw = e.target.value;
                    const v = raw === '' ? null : Number(raw);
                    setProject(p => ({ ...p, hours_per_story_point: raw === '' ? null : v }));
                  }}
                  onBlur={async (e) => {
                    const raw = e.target.value.trim();
                    const body = { hours_per_story_point: raw === '' ? null : Number(raw) };
                    try { const { data } = await axios.put(`${API}/projects/${projectId}`, body, h()); setProject(data); }
                    catch (err) { alert(err?.response?.data?.detail || 'Failed to save'); fetchAll(); }
                  }}
                  className="w-24 h-9 text-[13px]"
                />
                <span className="text-[11px] text-zinc-500 font-semibold">hours / SP</span>
              </div>
            </div>
          </div>
        </section>

        <Separator />

        {/* Integrations */}
        <ProjectIntegrationsSection projectId={projectId} canManage={canManage} />

        <Separator />

        {/* Custom Filters — create/edit/delete the project's saved filter library. */}
        <ProjectFiltersManager
          projectId={projectId}
          project={project}
          members={projectMembers}
          timezone={orgTimezone}
          tasks={tasks}
        />

        {canManage && (
          <>
            <Separator />
            {/* Danger zone — project deletion. Owners / admins / PMs only.
                Backend enforces role again as defense-in-depth. */}
            <section data-testid="danger-zone" className="rounded-xl border border-red-200 bg-red-50/50 p-5">
              <h2 className="font-['Outfit'] text-base font-semibold text-red-700 flex items-center gap-2">
                <Trash size={16} /> Danger Zone
              </h2>
              <p className="text-[12px] text-red-600/80 mt-1 mb-4">
                Deleting a project removes its tasks, sprints, comments, and integrations. This action is not easily reversible.
              </p>
              <Button
                onClick={handleDeleteProject}
                variant="outline"
                className="h-9 border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                data-testid="delete-project-btn"
              >
                <Trash size={14} className="mr-1.5" /> Delete this project
              </Button>
            </section>
          </>
        )}
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={(v) => { setShowAddMember(v); if (!v) { setAddMemberId(''); setAddMemberSearch(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-base font-bold">Add Project Member</DialogTitle>
            <DialogDescription className="text-[12px] text-zinc-500">Search and pick a teammate from your organization.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Inline searchable picker — replaces the previous Shadcn Select
                which rendered into a Radix portal that floated OUTSIDE the
                dialog body and felt disconnected. Pure-inline list keeps the
                whole picker inside the modal frame. */}
            <Input
              autoFocus
              placeholder="Search by name or email…"
              value={addMemberSearch}
              onChange={e => { setAddMemberSearch(e.target.value); setAddMemberId(''); }}
              className="h-9"
              data-testid="member-search-input"
            />
            <div className="max-h-60 overflow-y-auto rounded-md border border-zinc-200 divide-y divide-zinc-100" data-testid="member-search-results">
              {nonMemberOrg.length === 0 ? (
                <div className="px-3 py-4 text-[12px] text-zinc-400 text-center">All org members are already in this project.</div>
              ) : (() => {
                  const q = addMemberSearch.trim().toLowerCase();
                  const filtered = q
                    ? nonMemberOrg.filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
                    : nonMemberOrg;
                  if (!filtered.length) return <div className="px-3 py-4 text-[12px] text-zinc-400 text-center">No teammates match "{addMemberSearch}".</div>;
                  return filtered.map(m => {
                    const selected = addMemberId === m.user_id;
                    return (
                      <button
                        key={m.user_id}
                        type="button"
                        onClick={() => setAddMemberId(m.user_id)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${selected ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-zinc-50'}`}
                        data-testid={`member-option-${m.user_id}`}
                      >
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={m.avatar_url} />
                          <AvatarFallback className="text-[10px] bg-indigo-100 text-indigo-700">
                            {(m.name || m.email || '?').slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-zinc-900 truncate">{m.name || m.email}</div>
                          <div className="text-[11px] text-zinc-500 truncate">{m.email}</div>
                        </div>
                        {selected && <Badge className="text-[10px] bg-blue-600 text-white">Selected</Badge>}
                      </button>
                    );
                  });
                })()}
            </div>
            <Button onClick={handleAddMember} disabled={!addMemberId} className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white text-[12px]" data-testid="confirm-add-member">Add to Project</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Custom Field Dialog */}
      <Dialog open={showAddField} onOpenChange={setShowAddField}>
        <DialogContent className="sm:max-w-md" data-testid="add-field-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-base font-bold">Add Custom Field</DialogTitle>
            <DialogDescription className="text-[12px] text-zinc-500">Define a custom field for tasks in this project</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium">Field Name</Label>
              <Input data-testid="custom-field-name" value={fieldName} onChange={e => setFieldName(e.target.value)} placeholder="e.g., Sprint, Story Points, Environment" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium">Field Type</Label>
              <Select value={fieldType} onValueChange={setFieldType}>
                <SelectTrigger data-testid="custom-field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="select">Select (dropdown)</SelectItem>
                  <SelectItem value="richtext">Text Area</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {fieldType === 'select' && (
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium">Options (comma-separated)</Label>
                <Input data-testid="custom-field-options" value={fieldOptions} onChange={e => setFieldOptions(e.target.value)} placeholder="e.g., Sprint 1, Sprint 2, Sprint 3" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium">Applies To Work Types</Label>
              <p className="text-[10px] text-zinc-400 mb-2">Leave all unchecked to apply to all types</p>
              <div className="flex gap-3">
                {['task', 'story', 'bug'].map(wt => (
                  <label key={wt} className="flex items-center gap-1.5 text-[12px] text-zinc-700 cursor-pointer">
                    <Checkbox checked={fieldWorkTypes.includes(wt)} onCheckedChange={() => toggleWorkType(wt)} />
                    <span className="capitalize">{wt}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={handleAddField} disabled={!fieldName.trim()} className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white text-[12px]" data-testid="confirm-add-field">Add Custom Field</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Card Layout — checkbox list of toggleable fields that appear in the
// bottom strip of each Kanban card.
// Description is intentionally NOT a card option — it's a long-form
// field that doesn't fit in a chip.
const CARD_FIELD_OPTIONS = [
  // Identity & quick-glance
  { id: 'key', label: 'Ticket Key', help: 'e.g. MOB-12 — useful for sharing links and verbal references.' },
  { id: 'due_date', label: 'Due Date', help: 'Inline-editable due date; turns red when overdue.' },
  { id: 'priority', label: 'Priority', help: 'Coloured priority arrow (P1–P4).' },
  { id: 'story_points', label: 'Story Points', help: 'Inline-editable estimate. Hidden on cards without a value.' },
  { id: 'assignee', label: 'Assignee', help: 'Avatar of the developer the card is assigned to.' },
  // Extras — common on advanced boards
  { id: 'tags', label: 'Tags', help: 'Up to 3 tag pills shown above the bottom strip.' },
  { id: 'subtasks', label: 'Subtask Progress', help: 'Done/total badge on parent cards. Hidden when no subtasks.' },
  { id: 'reporter', label: 'Reporter', help: 'Tiny avatar of who raised the ticket.' },
  { id: 'sprint', label: 'Sprint', help: 'Sprint name pill — handy in multi-sprint reviews.' },
  { id: 'severity', label: 'Severity', help: 'For bug-tracking projects: Critical / High / etc.' },
  { id: 'estimate', label: 'Original Estimate', help: 'Hours estimate (when time tracking is enabled).' },
  { id: 'time_spent', label: 'Time Spent', help: 'Logged hours so far.' },
  { id: 'created_at', label: 'Created', help: 'Relative time the card was created.' },
  { id: 'updated_at', label: 'Updated', help: 'Relative time of the last edit.' },
];
const DEFAULT_CARD_FIELDS = ['key', 'due_date', 'priority', 'story_points', 'assignee'];

function CardLayoutEditor({ value, customFields, canEdit, onSave }) {
  // Per-project custom fields (excluding description-style long-form types)
  // become toggleable too. The admin can expose any of them on the card.
  const customCardOptions = (customFields || [])
    .filter(f => f.type !== 'richtext' && f.type !== 'longtext') // long-form fields aren't card-friendly
    .map(f => ({ id: f.field_id, label: f.name, help: `Custom field (${f.type}).`, custom: true }));

  // Hard limit — beyond this the bottom strip wraps and the card visually
  // breaks at narrow column widths. Backend enforces the same number.
  const MAX_CARD_FIELDS = 7;

  // null/undefined → use defaults so older projects "just work".
  const initial = Array.isArray(value) && value.length ? value : DEFAULT_CARD_FIELDS;
  const [fields, setFields] = useState(new Set(initial));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFields(new Set(Array.isArray(value) && value.length ? value : DEFAULT_CARD_FIELDS));
  }, [value]);

  const atLimit = fields.size >= MAX_CARD_FIELDS;

  const toggle = (id) => {
    if (!canEdit) return;
    setFields(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= MAX_CARD_FIELDS) {
          toast.error(`You can show at most ${MAX_CARD_FIELDS} fields on a card. Uncheck one before adding another.`);
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const dirty = (() => {
    const a = [...fields].sort().join(',');
    const b = [...initial].sort().join(',');
    return a !== b;
  })();

  const persist = async () => {
    setSaving(true);
    // Preserve the canonical default order for standard fields, then
    // append any custom field ids that were toggled on (in their
    // project-defined order).
    const stdOrder = CARD_FIELD_OPTIONS.map(f => f.id).filter(id => fields.has(id));
    const customOrder = customCardOptions.map(f => f.id).filter(id => fields.has(id));
    await onSave([...stdOrder, ...customOrder]);
    setSaving(false);
  };

  const resetToDefaults = () => {
    if (!canEdit) return;
    setFields(new Set(DEFAULT_CARD_FIELDS));
  };

  const renderOption = (opt, isCustom = false) => {
    const checked = fields.has(opt.id);
    const disabled = !canEdit || (atLimit && !checked); // prevent adding more when at limit
    const ringActive = isCustom ? 'border-violet-200 bg-violet-50/40' : 'border-blue-200 bg-blue-50/40';
    const ringHover = isCustom ? 'hover:border-violet-300' : 'hover:border-blue-300';
    return (
      <label
        key={opt.id}
        className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${checked ? ringActive : 'border-zinc-200 bg-white'} ${disabled ? 'cursor-not-allowed opacity-50' : (canEdit ? `cursor-pointer ${ringHover}` : 'cursor-default opacity-80')}`}
        data-testid={`card-field-toggle-${opt.id}`}
      >
        <Checkbox checked={checked} onCheckedChange={() => toggle(opt.id)} disabled={disabled} className="mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-zinc-800 truncate">{opt.label}</p>
          <p className="text-[10px] text-zinc-500 leading-snug">{opt.help}</p>
        </div>
      </label>
    );
  };

  return (
    <div className="border border-zinc-200 rounded-lg p-4 space-y-4" data-testid="card-layout-editor">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-zinc-500">
          <span className="font-semibold text-zinc-700">{fields.size}</span> of {MAX_CARD_FIELDS} fields selected
        </p>
        {atLimit && <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 ring-1 ring-amber-200 px-2 py-0.5 rounded-full" data-testid="card-fields-limit">Limit reached</span>}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-500 mb-2">Standard Fields</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CARD_FIELD_OPTIONS.map(opt => renderOption(opt, false))}
        </div>
      </div>
      {customCardOptions.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-zinc-500 mb-2">Custom Fields ({customCardOptions.length})</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {customCardOptions.map(opt => renderOption(opt, true))}
          </div>
        </div>
      )}
      {canEdit && (
        <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
          <button type="button" onClick={resetToDefaults} className="text-[11px] text-zinc-500 hover:text-zinc-800" data-testid="card-fields-reset">
            Reset to defaults
          </button>
          <Button size="sm" onClick={persist} disabled={!dirty || saving} className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-[11px]" data-testid="card-fields-save">
            {saving ? 'Saving…' : 'Save Layout'}
          </Button>
        </div>
      )}
    </div>
  );
}
